import { AlertTriangle } from 'lucide-react';
import { useT } from '../i18n';

// Linking a task to a milestone is optional. These helpers make a task
// without one visible where tasks are created or edited, and point to the
// Planning tab when the project has no milestone to attach it to yet.

type TFunc = (key: string, params?: Record<string, string | number>) => string;

/** The project has no milestone yet — the task (new, or already there with
 *  `existingTask`, e.g. moved in from another project) has none. */
export function NoMilestonesNotice({ projectId, existingTask }: { projectId: number; existingTask?: boolean }) {
  const { t } = useT();
  return (
    <div className="task-milestone-notice">
      <AlertTriangle size={13} />
      <span>
        {t(existingTask ? 'tasks.noMilestonesOnTask' : 'tasks.noMilestonesYet')} <a href={`#project?id=${projectId}`}>{t('tasks.openProjectPlanning')}</a>
      </span>
    </div>
  );
}

/** The task has no milestone (the project has some to choose from). */
export function MissingMilestoneNotice() {
  const { t } = useT();
  return (
    <div className="task-milestone-notice">
      <AlertTriangle size={13} />
      <span>{t('tasks.milestoneMissingOnTask')}</span>
    </div>
  );
}

/** A readable message for a task save refused by the server, falling back to its own text. */
export function taskSaveErrorMessage(err: Error & { code?: string; details?: { created_on?: string; from_milestone?: boolean } }, t: TFunc): string {
  if (err.code === 'MILESTONE_UNKNOWN') return t('tasks.milestoneUnknown');
  // Status changes are kept to the task's responsible, the PMO and the project's PMO accompanist.
  if (err.code === 'TASK_STATUS_FORBIDDEN') return t('tasks.statusForbidden');
  // [Proposition: timeline-taches] Planned date and due date required at creation.
  if (err.code === 'PLANNED_DATE_REQUIRED') return t('tlt.req.planned');
  if (err.code === 'DUE_DATE_REQUIRED') return t('tlt.req.due');
  // A due date can't be earlier than the task's creation.
  if (err.code === 'DUE_BEFORE_CREATION') {
    const created = err.details?.created_on;
    const date = created ? created.split('-').reverse().join('.') : '';
    return t(err.details?.from_milestone ? 'tlt.req.dueBeforeCreationMilestone' : 'tlt.req.dueBeforeCreation', { date });
  }
  return err.message;
}
